import { isRecord } from '@fruitful/fruitils';
import { SPROUT_JUDGE_SCORER_ID } from '@fruitful/llm-service-shared';
import type {
    ClientScoreRowData,
    DatasetExperiment,
    DatasetExperimentResult,
    MastraClient,
} from '@mastra/client-js';

import { findDataset } from './loadDataset';

const PAGE_SIZE = 100;
const MAX_PAGES = 50;

export interface ExperimentSummary {
    id: string;
    datasetId: string;
    name: string;
    targetId: string | null;
    status: DatasetExperiment['status'];
    datasetVersion: number | null;
    totalItems: number;
    succeededCount: number;
    failedCount: number;
    skippedCount: number;
    startedAt: string | Date | null;
}

export interface ExperimentScore {
    scorerId: string;
    scorerName: string;
    score: number | null;
    reason: string | null;
    error: string | null;
}

export interface ExperimentResult {
    id: string;
    itemId: string;
    caseId: string;
    group: string;
    prompt: string;
    extraTurnCount: number;
    groundTruth: string;
    output: string;
    error: string | null;
    scores: ExperimentScore[];
}

export interface ExperimentDetail {
    summary: ExperimentSummary;
    /** Group names in dataset order. */
    groupOrder: string[];
    results: ExperimentResult[];
}

const listAll = async <T>(
    load: (page: number) => Promise<{ rows: T[]; hasMore: boolean }>,
): Promise<T[]> => {
    const rows: T[] = [];
    let page = 0;
    for (;;) {
        if (page >= MAX_PAGES) {
            throw new Error('Experiment list did not terminate');
        }
        const listed = await load(page);
        rows.push(...listed.rows);
        if (!listed.hasMore || listed.rows.length === 0) {
            return rows;
        }
        page += 1;
    }
};

const timeValue = (value: string | Date | null): number => {
    if (value == null) {
        return 0;
    }
    const date = value instanceof Date ? value : new Date(value);
    const time = date.getTime();
    return Number.isNaN(time) ? 0 : time;
};

const toSummary = (experiment: DatasetExperiment): ExperimentSummary | null => {
    if (experiment.datasetId == null) {
        return null;
    }

    return {
        id: experiment.id,
        datasetId: experiment.datasetId,
        name:
            experiment.name != null && experiment.name !== ''
                ? experiment.name
                : 'Experiment',
        targetId: experiment.targetId,
        status: experiment.status,
        datasetVersion: experiment.datasetVersion,
        totalItems: experiment.totalItems,
        succeededCount: experiment.succeededCount,
        failedCount: experiment.failedCount,
        skippedCount: experiment.skippedCount,
        startedAt: experiment.startedAt ?? experiment.createdAt,
    };
};

const readText = (value: unknown): string => {
    if (typeof value === 'string') {
        return value;
    }
    if (value == null) {
        return '';
    }
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return '';
    }
};

const readPrompts = (input: unknown): string[] => {
    if (!Array.isArray(input)) {
        return [];
    }
    return input.filter(
        (prompt): prompt is string =>
            typeof prompt === 'string' && prompt !== '',
    );
};

interface StoredCase {
    caseId: string;
    group: string;
}

const readGroupOrder = (metadata: Record<string, unknown> | null): string[] => {
    const rawGroups = metadata?.groups;
    if (!Array.isArray(rawGroups)) {
        return [];
    }

    return rawGroups.filter(
        (name): name is string => typeof name === 'string' && name !== '',
    );
};

const toResult = (
    result: DatasetExperimentResult,
    cases: ReadonlyMap<string, StoredCase>,
): ExperimentResult => {
    const prompts = readPrompts(result.input);
    const prompt = prompts[0] ?? '';
    const storedCase = cases.get(result.itemId);

    const scores = Array.isArray(result.scores) ? result.scores : [];

    return {
        id: result.id,
        itemId: result.itemId,
        caseId: storedCase?.caseId ?? result.itemId,
        group: storedCase?.group ?? 'Ungrouped',
        prompt,
        extraTurnCount: Math.max(prompts.length - 1, 0),
        groundTruth: readText(result.groundTruth),
        output: readText(result.output),
        error: typeof result.error === 'string' ? result.error : null,
        scores: scores.map((score) => ({
            scorerId: score.scorerId,
            scorerName: score.scorerName,
            score: score.score,
            reason: score.reason,
            error: score.error,
        })),
    };
};

export interface StoredExperimentScore {
    itemId: string;
    scorerId: string;
    score: number | null;
    reason: string | null;
}

const scoreCreatedAtMs = (score: ClientScoreRowData): number => {
    if (score.createdAt === '') {
        return 0;
    }

    const parsed = new Date(score.createdAt).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
};

/** Judge scores for one experiment, newest first. Result rows do not include them. */
export const loadExperimentScores = async (
    client: MastraClient,
    experimentId: string,
): Promise<StoredExperimentScore[]> => {
    const rows: ClientScoreRowData[] = [];
    let page = 0;

    while (page < MAX_PAGES) {
        // Sequential pages. The next page depends on whether this one filled.
        const listed = await client.listScoresByRunId({
            runId: experimentId,
            page,
            perPage: PAGE_SIZE,
        });
        rows.push(...listed.scores);
        if (listed.scores.length < PAGE_SIZE) {
            break;
        }
        page += 1;
    }

    return rows
        .slice()
        .sort((left, right) => scoreCreatedAtMs(right) - scoreCreatedAtMs(left))
        .flatMap((score) => {
            if (score.entityId === '') {
                return [];
            }

            return [
                {
                    itemId: score.entityId,
                    scorerId: score.scorerId,
                    score: score.score,
                    reason:
                        score.reason != null && score.reason !== ''
                            ? score.reason
                            : null,
                },
            ];
        });
};

/** One judge score per case. The list is newest first, so a second judge row for the same case keeps the newer score. */
export const judgeScoreValues = (
    scores: readonly StoredExperimentScore[],
): number[] => {
    const seen = new Set<string>();
    const values: number[] = [];

    for (const score of scores) {
        if (
            score.scorerId !== SPROUT_JUDGE_SCORER_ID ||
            score.score == null ||
            seen.has(score.itemId)
        ) {
            continue;
        }

        seen.add(score.itemId);
        values.push(score.score);
    }

    return values;
};

/** Result rows do not include judge scores. Attach the ones stored for this experiment. */
export const attachExperimentScores = (
    detail: ExperimentDetail,
    scores: readonly StoredExperimentScore[],
): ExperimentDetail => {
    const byItem = new Map<string, ExperimentScore[]>();
    for (const score of scores) {
        const current = byItem.get(score.itemId) ?? [];
        current.push({
            scorerId: score.scorerId,
            scorerName: score.scorerId,
            score: score.score,
            reason: score.reason,
            error: null,
        });
        byItem.set(score.itemId, current);
    }

    return {
        summary: detail.summary,
        groupOrder: detail.groupOrder,
        results: detail.results.map((result) => {
            const attached = byItem.get(result.itemId);
            if (attached == null || attached.length === 0) {
                return result;
            }

            return { ...result, scores: attached };
        }),
    };
};

/** Experiments for an agent's published dataset, newest first. */
export const loadExperiments = async (
    client: MastraClient,
    agentId: string,
): Promise<ExperimentSummary[]> => {
    const dataset = await findDataset(client, agentId);
    if (dataset == null) {
        return [];
    }

    const experiments = await listAll(async (page) => {
        const listed = await client.listDatasetExperiments(dataset.id, {
            page,
            perPage: PAGE_SIZE,
        });
        return {
            rows: listed.experiments,
            hasMore: listed.pagination.hasMore,
        };
    });

    return experiments
        .flatMap((experiment) => {
            const summary = toSummary(experiment);
            return summary == null ? [] : [summary];
        })
        .sort(
            (left, right) =>
                timeValue(right.startedAt) - timeValue(left.startedAt),
        );
};

/** One experiment, including each case result. Null when it is not on this agent's dataset. */
export const loadExperiment = async (
    client: MastraClient,
    agentId: string,
    experimentId: string,
): Promise<ExperimentDetail | null> => {
    const dataset = await findDataset(client, agentId);
    if (dataset == null) {
        return null;
    }

    const experiments = await listAll(async (page) => {
        const listed = await client.listDatasetExperiments(dataset.id, {
            page,
            perPage: PAGE_SIZE,
        });
        return {
            rows: listed.experiments,
            hasMore: listed.pagination.hasMore,
        };
    });
    const experiment = experiments.find((item) => item.id === experimentId);
    const summary = experiment == null ? null : toSummary(experiment);
    if (summary == null) {
        return null;
    }

    const [items, results] = await Promise.all([
        listAll(async (page) => {
            const listed = await client.listDatasetItems(dataset.id, {
                page,
                perPage: PAGE_SIZE,
            });
            return {
                rows: listed.items,
                hasMore: listed.pagination.hasMore,
            };
        }),
        listAll(async (page) => {
            const listed = await client.listDatasetExperimentResults(
                dataset.id,
                experimentId,
                { page, perPage: PAGE_SIZE },
            );
            return {
                rows: listed.results,
                hasMore: listed.pagination.hasMore,
            };
        }),
    ]);
    const cases = new Map<string, StoredCase>();
    for (const item of items) {
        const metadata = isRecord(item.metadata) ? item.metadata : null;
        const caseId = metadata?.caseId;
        const group = metadata?.group;
        cases.set(item.id, {
            caseId:
                typeof caseId === 'string' && caseId !== ''
                    ? caseId
                    : (item.externalId ?? item.id),
            group:
                typeof group === 'string' && group !== '' ? group : 'Ungrouped',
        });
    }

    return {
        summary,
        groupOrder: readGroupOrder(dataset.metadata),
        results: results.map((result) => toResult(result, cases)),
    };
};
